import assertNever from "assert-never";
import React from "react";
import { Page } from "../../activities/AppPage";
import { API } from "../../API";
import { callNumber } from "../../external_link";
import useCaregiver from "../../hooks/useCaregiver";
import useClockedInStatus from "../../hooks/useClockedInStatus";
import useMixin from "../../hooks/useMixin";
import usePatientsState from "../../hooks/usePatientsState";
import { useUploadVerificationSelfieMutation } from "../../hooks/useUploadVerificationSelfieMutation";
import { isLoading, Loading, loading } from "../../Loading";
import {
  ClockInOutCallType,
  PatientTaskInstance,
  VisitInstanceClockinClockoutRequestParams,
  VisitInstanceDetails,
} from "../../messages";
import {
  AgencyId,
  PatientId,
  PatientTaskInstanceId,
  VisitInstanceId,
} from "../../messages/ObjectIds";
import { AppState } from "../../system/AppState";
import { fmap } from "../../utils/fmap";
import { withDefault } from "../../utils/withDefault";
import ChangesSavedBanner from "../ChangesSavedBanner";
import LoadingScaffold from "../LoadingScaffold";
import useContactSupport from "../useContactSupport";
import MedflytVisitLetsStartBottomSheet from "../VisitLetsStartBottomSheet/MedflytVisitLetsStartBottomSheet";
import VisitLetsStartBottomSheet from "../VisitLetsStartBottomSheet/VisitLetsStartBottomSheet";
import WithDutySheet from "../WithDutySheet";
import WithLetsStartBottomSheet from "../WithLetsStartBottomSheet";
import WithMedflytLetsStartBottomSheet from "../WithMedflytLetsStartBottomSheet";
import PatientNotExistScreen from "./PatientNotExistScreen";
import PatientScreen from "./PatientScreen";
import { PatientScreenState } from "./PatientTypes";
import { useCallNumber } from "./useCallNumber";
import { useOutsourcingAgencyQuery } from "../OutsourcingAgencyBanner/useOutsourcingAgencyQuery";

interface Props {
  api: API;
  appState: AppState;
  patientId: PatientId;
  onClock: (
    visitInstanceId: VisitInstanceId,
    clockInOutCallType: ClockInOutCallType,
    visitInstanceClockinClockoutRequestParams: VisitInstanceClockinClockoutRequestParams
  ) => Promise<boolean>;
  onRequestNavigate: (page: Page) => void;
  onClickChat: (patientId: PatientId) => void;
  onClickGoBack: () => void;
}

const PatientActivity = (props: Props) => {
  const [isCallPatientOpen, setCallPatientOpen] = React.useState(false);
  const [isDirectionsOpen, setDirectionsOpen] = React.useState(false);
  const { appAgenciesState, scheduledVisits, appTasksState } = useMixin(props.appState);
  const appPatientsState = usePatientsState(props.appState);
  const clockedInStatus = useClockedInStatus(props.appState);
  const caregiver = useCaregiver(props.appState);
  const [isChangesSavedBannerOpen, setChangesSavedBannerOpen] = React.useState(false);
  const [activeVisit, setActiveVisit] = React.useState<VisitInstanceDetails | Loading>(loading());
  const [isVisitBottomSheetOpen, setVisitBottomSheetOpen] = React.useState(false);
  const call = useCallNumber({ api: props.api, caregiver });

  const outsourcingAgencyQuery = useOutsourcingAgencyQuery({
    onRequestOutsourcingAgencyData: props.api.getOutsourcingAgencyData,
    patientId: props.patientId,
  });

  const handleClickListOfVisits = React.useCallback(() => {
    props.onRequestNavigate.call(null, {
      type: "PatientVisitsAndTasks",
      listType: "Visits",
      patientId: props.patientId,
    });
  }, [props.onRequestNavigate, props.patientId]);

  const handleClickListOfTasks = React.useCallback(() => {
    props.onRequestNavigate.call(null, {
      type: "PatientVisitsAndTasks",
      listType: "Tasks",
      patientId: props.patientId,
    });
  }, [props.onRequestNavigate, props.patientId]);

  const handleClickTask = React.useCallback(
    (taskInstanceId: PatientTaskInstanceId, initialScheduleDateTimePickerOpen: boolean) => {
      props.onRequestNavigate.call(null, {
        type: "PatientTaskInstance",
        patientTaskInstanceId: taskInstanceId,
        initialScheduleDateTimePickerOpen: initialScheduleDateTimePickerOpen,
      });
    },
    [props.onRequestNavigate]
  );

  const screen = ((): PatientScreenState => {
    if (
      isLoading(appPatientsState) ||
      isLoading(appAgenciesState) ||
      isLoading(scheduledVisits) ||
      isLoading(appTasksState) ||
      isLoading(clockedInStatus) ||
      isLoading(caregiver) ||
      outsourcingAgencyQuery.isLoading
    ) {
      return { status: "Loading" };
    }

    const patientFromState = appPatientsState.patients.find((x) => x.id === props.patientId);

    const agencyFromState = appAgenciesState.agencies.get(
      withDefault(
        fmap(patientFromState, (x) => x.agencyId),
        AgencyId.wrap(-1)
      )
    );

    const patientTasks: PatientTaskInstance[] = [];
    for (const taskInstance of appTasksState.tasks) {
      if (patientTasks.length < 3 && taskInstance.taskDetails.patientId === props.patientId) {
        patientTasks.push(taskInstance);
      }
    }

    if (patientFromState === undefined || agencyFromState === undefined) {
      return { status: "NotExist" };
    }

    return {
      status: "Ready",
      clockedInStatus: clockedInStatus,
      patient: patientFromState,
      agency: agencyFromState.agency,
      tasks: patientTasks,
      caregiver: caregiver,
      outsourcingAgencyData: outsourcingAgencyQuery.data ?? null,
    };
  })();

  const handleClickChat = React.useCallback(() => {
    if (screen.status === "Ready") {
      props.onClickChat.call(null, screen.patient.id);
    }
  }, [props.onClickChat, screen]);

  const handleClickCallPatient = React.useCallback(() => setCallPatientOpen(true), []);
  const handleRequestCloseCallPatient = React.useCallback(() => setCallPatientOpen(false), []);

  const handleClickDirections = React.useCallback(() => setDirectionsOpen(true), []);
  const handleRequestCloseDirections = React.useCallback(() => setDirectionsOpen(false), []);

  const handleClickCallAgency = React.useCallback(() => {
    if (screen.status === "Ready" && screen.agency.officePhone !== null) {
      callNumber(screen.agency.officePhone);
    }
  }, [screen]);

  const uploadCerificationSelfieMutation = useUploadVerificationSelfieMutation({
    api: props.api,
  });

  const handleOpenChangesSavedBanner = () => {
    setChangesSavedBannerOpen(true);
  };

  const handleCloseChangesSavedBanner = () => {
    setChangesSavedBannerOpen(false);
  };

  const handleHideOrShowVisitBottomSheet = (isOpen: boolean) => {
    setVisitBottomSheetOpen(isOpen);
  };

  const handleOpenVisitBottomSheet = (visitInstance: VisitInstanceDetails) => {
    setActiveVisit(visitInstance);
    setVisitBottomSheetOpen(true);
  };

  const contactSupport = useContactSupport({
    api: props.api,
    appState: props.appState,
    onRequestNavigate: props.onRequestNavigate,
    onClickNewChatWithMedflyt: () => props.onClickChat(props.patientId),
  });

  const handleUpdateActiveVisit = (visitInstanceId: VisitInstanceId) => {
    if (isLoading(props.appState.scheduledVisits)) {
      return;
    }
    const visitInstance = props.appState.scheduledVisits.scheduledVisitDetails.find(
      (x) => x.id === visitInstanceId
    );
    if (visitInstance === undefined) {
      return;
    }
    handleOpenVisitBottomSheet(visitInstance);
  };

  const handleSubmitDutySheet = (visitInstanceId: VisitInstanceId) => {
    props.appState.setVisitInstanceDutySheetCompleted(visitInstanceId, true);
    if (!isLoading(activeVisit)) {
      setActiveVisit({ ...activeVisit, isDutySheetCompleted: true });
    }
  };

  const handleUploadVerificationSelfie = (
    visitInstanceId: VisitInstanceId,
    imageBase64: string,
    clockType: ClockInOutCallType
  ): Promise<void> => {
    return uploadCerificationSelfieMutation.mutateAsync({
      clockType,
      imageBase64,
      visitInstanceId,
    });
  };

  const visitLetsStartBottomSheet = (() => {
    if (isLoading(activeVisit) || isLoading(caregiver)) {
      return null;
    }
    return (
      <WithDutySheet activeVisit={activeVisit} api={props.api}>
        {caregiver.isCaregiverOfMedFlyt
          ? (initialDutySheetData) => (
              <WithMedflytLetsStartBottomSheet
                activeEntity={{ type: "visit", visit: activeVisit }}
                api={props.api}
                appState={props.appState}
                initialDutySheetData={initialDutySheetData}
                isOpen={isVisitBottomSheetOpen}
                onHideOrShowVisitBottomSheet={handleHideOrShowVisitBottomSheet}
                onOpenChangesSavedBanner={handleOpenChangesSavedBanner}
                onUpdateActiveVisit={handleUpdateActiveVisit}
                onSubmitDutySheet={handleSubmitDutySheet}
              >
                {(bottomSheetProps) => (
                  <MedflytVisitLetsStartBottomSheet
                    {...bottomSheetProps}
                    contactSupport={contactSupport}
                  />
                )}
              </WithMedflytLetsStartBottomSheet>
            )
          : (initialDutySheetData) => (
              <WithLetsStartBottomSheet
                activeVisit={activeVisit}
                api={props.api}
                appState={props.appState}
                initialDutySheetData={initialDutySheetData}
                isOpen={isVisitBottomSheetOpen}
                onHideOrShowVisitBottomSheet={handleHideOrShowVisitBottomSheet}
                onOpenChangesSavedBanner={handleOpenChangesSavedBanner}
                onUpdateActiveVisit={handleUpdateActiveVisit}
                onSubmitDutySheet={handleSubmitDutySheet}
              >
                {(bottomSheetProps) => (
                  <VisitLetsStartBottomSheet
                    {...bottomSheetProps}
                    contactSupport={contactSupport}
                  />
                )}
              </WithLetsStartBottomSheet>
            )}
      </WithDutySheet>
    );
  })();

  switch (screen.status) {
    case "Loading":
      return <LoadingScaffold onClickGoBack={props.onClickGoBack} />;
    case "NotExist":
      return <PatientNotExistScreen onClickGoBack={props.onClickGoBack} />;
    case "Ready":
      return (
        <>
          <PatientScreen
            patient={screen.patient}
            agency={screen.agency}
            isCallOpen={isCallPatientOpen}
            isDirectionsOpen={isDirectionsOpen}
            tasksPreview={screen.tasks}
            clockedInStatus={screen.clockedInStatus}
            caregiverId={screen.caregiver.id}
            isCaregiverOfMedflyt={screen.caregiver.isCaregiverOfMedFlyt}
            visitLetsStartBottomSheet={visitLetsStartBottomSheet}
            onClickChat={handleClickChat}
            onClickCallPatient={handleClickCallPatient}
            onClickPatientPhone={(destination) => {
              handleRequestCloseCallPatient();
              call({
                agencyId: screen.patient.agencyId,
                name: screen.patient.firstName,
                destination: destination,
              });
            }}
            onRequestCloseCallPatient={handleRequestCloseCallPatient}
            onClickDirections={handleClickDirections}
            onRequestCloseDirections={handleRequestCloseDirections}
            onClickListOfVisits={handleClickListOfVisits}
            onClickListOfTasks={handleClickListOfTasks}
            onClickTask={handleClickTask}
            onClickChatWithAgency={handleClickChat}
            onClickCallAgency={handleClickCallAgency}
            outsourcingAgencyData={screen.outsourcingAgencyData}
            onClickGoBack={props.onClickGoBack}
            onClock={props.onClock}
            onRequestOpenVisitLetsStartBottomSheet={handleOpenVisitBottomSheet}
            onRequestNavigate={props.onRequestNavigate}
            onUploadVerificationSelfie={handleUploadVerificationSelfie}
            onRequestVisitAdjustmentCheck={props.api.getCaregiverCanAdjustVisitInstanceTimes}
            onCheckIfDocumentsSubmitted={props.api.checkIfAllDocumentsSubmitted}
            onCaregiverMarkVisitAsMissed={props.api.caregiverMarkVisitAsMissed}
            onRequestOutsourcingAgencyData={props.api.getOutsourcingAgencyData}
          />
          <ChangesSavedBanner
            isOpen={isChangesSavedBannerOpen}
            onClose={handleCloseChangesSavedBanner}
          />
        </>
      );
    default:
      assertNever(screen);
  }
};

export default React.memo(PatientActivity);
